import { useQuery, useMutation, UseQueryOptions } from '@tanstack/react-query';
import {
  CreateUserRequestBody,
  GetUsersRequest,
  GetUsersResponse,
  LegacySequelizeUser,
} from '@harvestiq/iiq-common';
import { UnblockUsersRequestBody } from '@harvestiq/iiq-common/users/unblock-users';
import { BlockUsersRequestBody } from '@harvestiq/iiq-common/users/block-users';
import { SendWelcomeEmailRequestBody } from '@harvestiq/iiq-common/users/send-welcome-email';
import { useInsureIQApiClient } from '../contexts/InsureIQApiContextProvider';
import { UserIdParam } from '@harvestiq/iiq-common/users/users-api-params';
import { UpdateUserRequest } from '@harvestiq/iiq-common/users/update-user';
import { useToast, useUser } from '../contexts/index';

export const useGetUserById = (
  id: string,
  options: { enabled?: boolean } = {}
) => {
  const { insureIQApiClient } = useInsureIQApiClient();

  return useQuery({
    ...(options ?? {}),
    queryKey: ['useGetUserById', id],
    queryFn: async () => {
      const user = await insureIQApiClient.getUserById({ userId: id });
      return user;
    },
  });
};

type UseGetUsersParams = {
  params?: GetUsersRequest;
  queryKey?: (string | GetUsersRequest)[];
  options?: Partial<
    UseQueryOptions<
      GetUsersResponse,
      Error,
      GetUsersResponse,
      (string | GetUsersRequest)[]
    >
  >;
};

export function useGetUsers({
  queryKey = ['getUsers'],
  params,
  options,
}: UseGetUsersParams = {}) {
  const { insureIQApiClient } = useInsureIQApiClient();

  const qryKey: UseGetUsersParams['queryKey'] = Array.isArray(queryKey)
    ? queryKey
    : [queryKey];
  if (params && typeof params === 'object') {
    qryKey.push(params);
  }

  return useQuery({
    ...options,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: qryKey,
    queryFn: async () => {
      const response = await insureIQApiClient.getUsers(params);
      if (!response) {
        console.error('No data returned from getUsers');
        return { data: response };
      }
      return { data: response };
    },
  });
}

export function useBlockUsers() {
  const { insureIQApiClient } = useInsureIQApiClient();

  return useMutation({
    mutationFn: async (body: BlockUsersRequestBody) => {
      const response = await insureIQApiClient.blockUsers(body);

      if (!response || 'errors' in response) {
        throw new Error('There was an error banning users. Please try again.');
      }
      return response;
    },
  });
}

export function useUnblockUsers() {
  const { insureIQApiClient } = useInsureIQApiClient();

  return useMutation({
    mutationFn: async (body: UnblockUsersRequestBody) => {
      const response = await insureIQApiClient.unblockUsers(body);

      if (!response || 'errors' in response) {
        throw new Error(
          'There was an error restoring users. Please try again.'
        );
      }
      return response;
    },
  });
}

export function useUpdateUser() {
  const { insureIQApiClient } = useInsureIQApiClient();

  return useMutation({
    mutationFn: async ({
      userId,
      body,
    }: {
      userId: UserIdParam;
      body: UpdateUserRequest;
    }) => {
      const response = await insureIQApiClient.updateUser({ userId }, body);
      if (!response || 'errors' in response) {
        throw new Error(
          'There was an error updating the user. Please try again.'
        );
      }
      return response;
    },
  });
}

export function useCreateUser() {
  const { insureIQApiClient } = useInsureIQApiClient();

  return useMutation({
    mutationFn: async (body: CreateUserRequestBody) => {
      const response = await insureIQApiClient.createUser(body);

      if (!response) {
        throw new Error(
          'There was an error creating the user. Please try again.'
        );
      } else if ('errors' in response) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      } else {
        return response.data.user;
      }
    },
  });
}

export function useSendWelcomeEmail() {
  const { insureIQApiClient } = useInsureIQApiClient();

  return useMutation({
    mutationFn: async (body: SendWelcomeEmailRequestBody) => {
      const response = await insureIQApiClient.sendWelcomeEmail(body);

      if (!response || 'errors' in response) {
        throw new Error(
          'There was an error sending welcome emails. Please try again.'
        );
      }

      return response;
    },
  });
}

/**
 * Handles sending SMS opt-in confirmation.
 * Note: Actual opt-in/opt-out state is managed by the backend via the `smsOptIn` parameter.
 */

type UserOptInAndOutFields = Pick<
  LegacySequelizeUser,
  'id' | 'smsOptInRequestedAt' | 'smsOptOutRequestedAt'
>;

export function useSmsOptInConfirmation() {
  const { getMe } = useUser();
  const { insureIQApiClient } = useInsureIQApiClient();
  const { errorToast } = useToast();

  // Helper function to check if a user is currently opted in
  const isCurrentlyOptedIn = (user?: UserOptInAndOutFields) => {
    return !!user?.smsOptInRequestedAt && !user?.smsOptOutRequestedAt;
  };

  // Sends confirmation SMS when opting in or out
  const sendOptInOrOptOutConfirmationSms = async ({
    optIn,
    user,
    isNewUser = false,
  }: {
    optIn: boolean;
    user: UserOptInAndOutFields | undefined;
    isNewUser?: boolean;
  }) => {
    if (!user?.id) {
      console.error('Cannot send SMS confirmation: No user ID provided');
      errorToast('Failed to send SMS confirmation: User missing.');
      return;
    }

    const currentlyOptedIn = isCurrentlyOptedIn(user);

    // Send opt-in confirmation when opting in from an null/opted-out state
    if ((optIn && !currentlyOptedIn) || (isNewUser && optIn)) {
      try {
        await insureIQApiClient.sendOptInConfirmation({ recipientId: user.id });
        await getMe();
      } catch (error) {
        console.error('Failed to send SMS opt-in confirmation:', error);
        errorToast('Failed to send SMS opt-in confirmation. Please try again.');
      }
    }
    // Send opt-out confirmation when opting out from an opted-in state
    else if (!optIn && currentlyOptedIn) {
      try {
        await insureIQApiClient.sendOptOutConfirmation({
          recipientId: user.id,
        });
        await getMe();
      } catch (error) {
        console.error('Failed to send SMS opt-out confirmation:', error);
        errorToast(
          'Failed to send SMS opt-out confirmation. Please try again.'
        );
      }
    }
  };

  return { isCurrentlyOptedIn, sendOptInOrOptOutConfirmationSms };
}
